import React from 'react';

/**
 * Enhanced loading skeleton specifically for analytics components
 * Provides better perceived performance with realistic loading states
 */

export const MetricsGridSkeleton = ({ columns = 4, rows = 1 }) => {
  return (
    <div className="space-y-6">
      {[...Array(rows)].map((_, rowIndex) => (
        <dl key={rowIndex} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(columns)].map((_, index) => (
            <div key={index} className="overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 py-5 shadow border border-gray-200 dark:border-gray-700">
              <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4"></div>
              </dt>
              <dd className="mt-1">
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/2"></div>
              </dd>
              <dd className="mt-1">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-full"></div>
              </dd>
            </div>
          ))}
        </dl>
      ))}
    </div>
  );
};

export const ChartSkeleton = ({ height = 200, title = true }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
      {title && (
        <div className="mb-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/3"></div>
        </div>
      )}
      <div 
        className="bg-gray-100 dark:bg-gray-700 rounded animate-pulse"
        style={{ height: `${height}px` }}
      >
        {/* Simulate chart elements */}
        <div className="flex items-end justify-around h-full p-4">
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              className="bg-gray-200 dark:bg-gray-600 rounded-t animate-pulse"
              style={{
                height: `${Math.random() * 60 + 20}%`,
                width: '8%'
              }}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const TableSkeleton = ({ rows = 5, columns = 4, hasHeader = true }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          {hasHeader && (
            <thead className="bg-gray-50 dark:bg-gray-700 dark:bg-gray-900">
              <tr>
                {[...Array(columns)].map((_, index) => (
                  <th key={index} className="px-4 py-3">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse"></div>
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {[...Array(rows)].map((_, rowIndex) => (
              <tr key={rowIndex}>
                {[...Array(columns)].map((_, colIndex) => (
                  <td key={colIndex} className="px-4 py-3">
                    <div 
                      className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
                      style={{ width: `${Math.random() * 40 + 60}%` }}
                    ></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const ConversationListSkeleton = ({ rows = 10 }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      {/* Header skeleton */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/4"></div>
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32"></div>
        </div>
      </div>
      
      {/* List items skeleton */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {[...Array(rows)].map((_, index) => (
          <div key={index} className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex-1 space-y-2">
                <div className="flex items-center space-x-4">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20"></div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-12"></div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-16"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const SystemHealthSkeleton = () => {
  return (
    <div className="space-y-6">
      {/* Health metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/2"></div>
              <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
            </div>
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-full"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3"></div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Charts skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartSkeleton height={250} />
        <ChartSkeleton height={250} />
      </div>
      
      {/* Alerts skeleton */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/4 mb-4"></div>
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded dark:bg-gray-900">
              <div className="h-4 w-4 bg-gray-200 dark:bg-gray-600 rounded-full animate-pulse"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-3/4"></div>
              </div>
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-16"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const CallSuccessRateSkeleton = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
      {/* Header with tabs */}
      <div className="flex items-center justify-between mb-6">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/3"></div>
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="h-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-16"></div>
          ))}
        </div>
      </div>
      
      {/* Overview metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg dark:bg-gray-900">
            <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-16 mx-auto mb-2"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded animate-pulse w-20 mx-auto"></div>
          </div>
        ))}
      </div>
      
      {/* Chart area */}
      <div className="h-48 bg-gray-100 dark:bg-gray-700 rounded animate-pulse"></div>
    </div>
  );
};

export const AnalyticsDashboardSkeleton = () => {
  return (
    <div className="space-y-6">
      {/* Time range selector skeleton */}
      <div className="flex items-center justify-between">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32"></div>
      </div>
      
      {/* Metrics grid */}
      <MetricsGridSkeleton columns={4} />
      
      {/* Charts section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartSkeleton height={300} />
        <CallSuccessRateSkeleton />
      </div>
      
      {/* Additional content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TableSkeleton rows={8} columns={5} />
        </div>
        <div>
          <ChartSkeleton height={200} />
        </div>
      </div>
    </div>
  );
};

const AnalyticsLoadingSkeleton = {
  MetricsGridSkeleton,
  ChartSkeleton,
  TableSkeleton,
  ConversationListSkeleton,
  SystemHealthSkeleton,
  CallSuccessRateSkeleton,
  AnalyticsDashboardSkeleton
};

export default AnalyticsLoadingSkeleton;
